import { convertToSnakeCase } from '@sgcmpteltd/uikit-react/utils/string';

import type {
  NewCarFuelType,
  NewCarSortType,
} from '@/apis/server/new-car/v1/types';

import type { OnSearchData } from '../../types';
import { RESULT_PER_PAGE_NUM_LIMIT } from './constants';

export const createQueryString = ({ query }: OnSearchData) => {
  const params = new URLSearchParams();

  const searchQueryEntries = Object.entries(query);

  for (const [key, value] of searchQueryEntries) {
    if (value !== undefined) {
      params.set(convertToSnakeCase(key), `${value}`);
    }
  }

  return params.toString();
};

export const createOldSiteQueryString = (
  isDesktop: boolean,
) => ({ query }: OnSearchData) => {
  const vtMapping = (vt: string) => {
    switch (vt) {
      case 'hybrid':
        return 'Hybrid';
      case 'hatchback':
        return 'Hatchback';
      case 'electric':
        return 'Electric';
      case 'luxury':
        return 'Luxury';
      case 'mpv':
        return 'MPV';
      case 'sports':
        return 'Sports';
      case 'stationwagon':
        return 'Stationwagon';
      case 'suv':
        return 'SUV';
      case 'commercial':
        return 'Commercial';
      case 'passenger':
        return 'Passenger';
      case 'sedan':
        return 'Sedan';
      default:
        return '';
    }
  };

  const vtsMapping = (vts: string) => {
    switch (vts) {
      case 'commercial':
        return '1';
      case 'sedan':
        return '4';
      case 'luxury':
        return '5';
      case 'hatchback':
        return '6';
      case 'mpv':
        return '7';
      case 'suv':
        return '8';
      case 'sports':
        return '9';
      case 'stationwagon':
        return '10';
      case 'hybrid':
        return '11';
      case 'electric':
        return '12';
      default:
        return '';
    }
  };

  const dtMapping = (dt: string) => {
    switch (dt) {
      case 'ad':
        return 'A';
      case 'pi':
        return 'P';
      case 'a':
        return 'CoeA';
      case 'b':
        return 'CoeB';
      default:
        return '';
    }
  };

  const fueMapping = (fue: NewCarFuelType) => {
    switch (fue) {
      case 'petrol':
        return 'p';
      case 'diesel':
        return 'd';
      case 'petrol-electric':
        return 'r';
      case 'diesel-electric':
        return 'i';
      case 'electric':
        return 'e';
      default:
        return '';
    }
  };

  const sortMapping = (sort: NewCarSortType) => {
    switch (sort) {
      case 'release_date':
        return 'release';
      case 'price':
        return 'price';
      case 'name':
        return 'model';
      case 'rating':
        return 'rating';
      default:
        return '';
    }
  };

  const params = new URLSearchParams();

  if (query.q) {
    params.set('MOD', query.q);
  }

  if (query.dealerId) {
    params.set('DID', query.dealerId.toString());
  }

  if (query.minPrice) {
    params.set('PR1', (0.001 * query.minPrice).toString());
  }

  if (query.maxPrice) {
    params.set('PR2', (0.001 * query.maxPrice).toString());
  }

  if (isDesktop) {
    if (query.minDepreciation) {
      params.set('DEP1', (0.001 * query.minDepreciation).toString());
    }

    if (query.maxDepreciation) {
      params.set('DEP2', (0.001 * query.maxDepreciation).toString());
    }
  } else if (query.minDepreciation && query.maxDepreciation) {
    params.set('DEP', `${0.001 * query.minDepreciation}-${0.001 * query.maxDepreciation}`);
  }

  if (query.vehicleType) {
    query.vehicleType.split(',').forEach((v) => {
      if (isDesktop) {
        params.append('VTS[]', vtsMapping(v));
      } else {
        params.set('VT', vtMapping(v));
      }
    });
  }

  if (query.category) {
    params.set('DT', dtMapping(query.category));
  }

  if (query.transmission) {
    params.set('TRN', query.transmission.toUpperCase());
  }

  if (query.minPowerOutput && query.maxPowerOutput) {
    params.set('POW_C', '2');
    params.set('POW', query.minPowerOutput.toString());
  } else if (query.minPowerOutput && !query.maxPowerOutput) {
    params.set('POW_C', '1');
    params.set('POW', query.minPowerOutput.toString());
  } else if (!query.minPowerOutput && query.maxPowerOutput) {
    params.set('POW_C', '0');
    params.set('POW', query.maxPowerOutput.toString());
  }

  if (query.minOmv && query.maxOmv) {
    params.set('OMV_C', '2');
    params.set('OMV', query.minOmv.toString());
  } else if (query.minOmv && !query.maxOmv) {
    params.set('OMV_C', '1');
    params.set('OMV', query.minOmv.toString());
  } else if (!query.minOmv && query.maxOmv) {
    params.set('OMV_C', '0');
    params.set('OMV', query.maxOmv.toString());
  }

  if (query.minFuelEconomy && query.maxFuelEconomy) {
    params.set('FUEECO_C', '2');
    params.set('FUEECO', query.minFuelEconomy.toString());
  } else if (query.minFuelEconomy && !query.maxFuelEconomy) {
    params.set('FUEECO_C', '1');
    params.set('FUEECO', query.minFuelEconomy.toString());
  } else if (!query.minFuelEconomy && query.maxFuelEconomy) {
    params.set('FUEECO_C', '0');
    params.set('FUEECO', query.maxFuelEconomy.toString());
  }

  if (query.fuelType) {
    params.set('FUE', fueMapping(query.fuelType));
  }

  if (query.maxEngineCapacity === 661) {
    params.set('EGC', '5');
  }

  if (
    query.minEngineCapacity === 661
    && query.maxEngineCapacity === 1600) {
    params.set('EGC', '4');
  }

  if (
    query.minEngineCapacity === 661
    && query.maxEngineCapacity === 1600) {
    params.set('EGC', '4');
  }

  if (
    query.minEngineCapacity === 1601
    && query.maxEngineCapacity === 2000) {
    params.set('EGC', '3');
  }

  if (
    query.minEngineCapacity === 2001
    && query.maxEngineCapacity === 3000) {
    params.set('EGC', '2');
  }

  if (query.minEngineCapacity === 3000) {
    params.set('EGC', '1');
  }

  if (query.minDriveRange) {
    params.set('DR1', query.minDriveRange.toString());
  }

  if (query.maxDriveRange) {
    params.set('DR2', query.maxDriveRange.toString());
  }

  if (query.country) {
    params.set('REGION', query.country);
  }

  if (query.sortBy && query.order) {
    params.set('ORD', `${sortMapping(query.sortBy)}${query.order.toUpperCase()}`);
  }

  if (query.limit) {
    params.set('RPG', query.limit.toString());
  }

  if (query.page) {
    params.set('BRSR', (
      (query.page - 1) * (query.limit ?? RESULT_PER_PAGE_NUM_LIMIT)
    ).toString());
  }

  if (query.make) {
    params.set('MAK', query.make);

    if (!query.q) {
      params.set('MOD', query.make);
    }
  }

  return params.toString();
};
